import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardList, Loader2, Sparkles, Pencil, Users, Eye, EyeOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API as RL_API } from '@/components/resource-library/resourceLibraryApi';
import { getSet, extractSet, deleteSet, setItemStatus, KIND_META } from '@/components/activities/activitiesApi';
import { ItemEditorDialog } from '@/components/activities/ItemEditorDialog';
import { SubmissionsDialog } from '@/components/activities/SubmissionsDialog';
import { TestsTable } from '@/pages/dashboard/QuickCheckProgressPage';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

export default function ActivitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedItem = searchParams.get('item');
  const [pairs, setPairs] = useState([]);
  const [selected, setSelected] = useState('');
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${RL_API}/api/resource-library/subjects`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${RL_API}/api/activities/teacher/summary`, { headers: authHeaders() }).then((r) => (r.ok ? r.json() : { items: [] })).catch(() => ({ items: [] })),
    ]).then(([d, summary]) => {
      const list = (d.subjects || []).filter((p) => p.has_files);
      const all = list.length ? list : d.subjects || [];
      setPairs(all);
      const qs = searchParams.get('subject');
      const qy = searchParams.get('year');
      const withWork = (summary.items || []).find((i) => i.pending > 0) || (summary.items || [])[0];
      const pick = (qs && qy) ? { subject: qs, year_level: qy } : withWork || all[0];
      if (pick) setSelected(`${pick.subject}|${pick.year_level}`);
    });
  }, [searchParams]);

  const [subject, yearStr] = selected.split('|');
  const load = useCallback(() => { if (subject) getSet(subject, yearStr).then(setData); }, [subject, yearStr]);
  useEffect(() => { setData(null); load(); }, [load]);

  useEffect(() => {
    if (wantedItem && data?.items && !viewing) {
      const it = data.items.find((x) => x.id === wantedItem);
      if (it) {
        setViewing(it);
        const next = new URLSearchParams(searchParams); next.delete('item'); setSearchParams(next, { replace: true });
      }
    }
  }, [wantedItem, data]);

  const extracting = data?.set?.status === 'extracting';
  useEffect(() => {
    if (!extracting) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [extracting, load]);

  const generate = async () => {
    if (data?.set && !window.confirm('Re-generate from the booklet? Existing questions, edits and student submissions for this subject/year will be deleted.')) return;
    setBusy(true);
    try { await extractSet(subject, yearStr); toast.success('Reading the booklet and answer key — this takes a few minutes'); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const toggle = async (it) => { await setItemStatus(it.id, it.status === 'published' ? 'draft' : 'published'); load(); };
  const removeAll = async () => { if (window.confirm('Delete all activities and tests for this subject/year?')) { await deleteSet(data.set.id); load(); } };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="activities-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="h-6 w-6 text-violet-600" />Activities & Chapter Tests</h1>
          <p className="text-sm text-slate-500 mt-1">Generated from the Activities Booklet + Answer Key. Review, publish to students, see answers, then push marks to the Grade Book.</p>
        </div>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="border rounded-md px-3 py-2 text-sm bg-white min-w-[220px]" data-testid="activities-subject-select">
          {pairs.map((p) => <option key={`${p.subject}|${p.year_level}`} value={`${p.subject}|${p.year_level}`}>Year {p.year_level} {p.subject}</option>)}
        </select>
      </div>

      {data === null ? <Loader2 className="h-6 w-6 animate-spin text-emerald-600" /> : (
        <>
          <div className="rounded-xl border bg-violet-50/50 p-4 flex flex-wrap items-center gap-3" data-testid="activities-set-status">
            <Sparkles className="h-5 w-5 text-violet-600" />
            <div className="flex-1 text-sm">
              {!data.set && <span>No activities generated yet for Year {yearStr} {subject}. Upload the Activities Booklet and Answer Key in the Resource Library, then click Generate.</span>}
              {extracting && <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />AI is reading the booklet… {data.set.progress}</span>}
              {data.set?.status === 'ready' && <span>{data.items.length} items ready · {data.items.filter((i) => i.status === 'published').length} published to students{data.quick_check_chapters ? ` · ${data.quick_check_chapters} Quick Check practice sets live in the student portal` : ''}</span>}
              {data.set?.status === 'failed' && <span className="text-rose-700">Generation failed: {data.set.error}</span>}
            </div>
            <Button size="sm" variant={data.set ? 'outline' : 'default'} disabled={busy || extracting} onClick={generate} data-testid="activities-generate-btn">
              <Sparkles className="h-4 w-4 mr-1" />{data.set ? 'Re-generate' : 'Generate from booklet'}
            </Button>
            {data.set && !extracting && <Button size="icon" variant="ghost" className="text-rose-600" onClick={removeAll} data-testid="activities-delete-set"><Trash2 className="h-4 w-4" /></Button>}
          </div>

          <div className="space-y-3" data-testid="activities-items">
            {data.items.map((it) => (
              <div key={it.id} className="rounded-xl border bg-white p-4 flex flex-wrap items-center gap-3 shadow-sm" data-testid={`activity-item-${it.code}`}>
                <span className="h-10 w-10 rounded-lg bg-slate-800 text-white font-bold flex items-center justify-center text-sm">{it.code}</span>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold text-slate-900">{it.title}</p>
                  <p className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                    <Badge variant="outline" className={KIND_META[it.kind]?.className}>{KIND_META[it.kind]?.label}</Badge>
                    <span>{(it.questions || []).length} questions · {it.total_marks} marks</span>
                    <span>{it.submissions} submitted{it.submissions ? ` · avg ${Math.round(it.average)}%` : ''}</span>
                  </p>
                </div>
                <Badge variant="outline" className={it.status === 'published' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600'} data-testid={`activity-status-${it.code}`}>{it.status === 'published' ? 'Visible to students' : 'Draft'}</Badge>
                <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`activity-edit-${it.code}`}><Pencil className="h-4 w-4 mr-1" />Review</Button>
                <Button size="sm" variant="outline" onClick={() => setViewing(it)} data-testid={`activity-submissions-${it.code}`}><Users className="h-4 w-4 mr-1" />Answers</Button>
                <Button size="sm" className={it.status === 'published' ? 'bg-slate-600 hover:bg-slate-700' : 'bg-emerald-600 hover:bg-emerald-700'} onClick={() => toggle(it)} data-testid={`activity-toggle-${it.code}`}>
                  {it.status === 'published' ? <><EyeOff className="h-4 w-4 mr-1" />Hide</> : <><Eye className="h-4 w-4 mr-1" />Publish</>}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      {subject && (
        <section className="space-y-3 pt-4 border-t" data-testid="interactive-tests-section">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Interactive Tests & Exams</h2>
            <p className="text-sm text-slate-500">Topic tests and practice exams built in the portal (e.g. Accounting). Attempts and best scores per student.</p>
          </div>
          <TestsTable subject={subject} yearLevel={Number(yearStr)} />
        </section>
      )}

      {editing && <ItemEditorDialog item={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {viewing && <SubmissionsDialog item={viewing} onClose={() => { setViewing(null); load(); }} />}
    </div>
  );
}
